import { StateCreator, create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  BranchOffice,
  branchOwnsInventory,
} from '../models/branch-office.model';
import { useAuthStore } from './auth.store';

/**
 * Callback that guards branch changes. Return `true` to allow the change,
 * `false` to block it.  When blocked, the caller (BranchSelector) should
 * show a confirmation dialog and let the user decide.
 */
export type BranchChangeGuardFn = (nextBranchId: string) => boolean;

export type BranchStoreSlice = {
  selectedBranchOfficeId?: string;
  branchOffices: BranchOffice[];
  setBranchOffices: (branchOffices: BranchOffice[]) => void;
  setSelectedBranchOfficeId: (id: string) => void;
  /**
   * The server denied a branch (`BRANCH_ACCESS_DENIED`): drop it from the
   * reachable list and, when it was the selection, fall back to the first
   * remaining branch ('' when none is left). Idempotent — a second denial
   * for the same branch is a no-op, so concurrent 403s recover once.
   */
  revokeBranch: (branchOfficeId: string) => void;
  /** Register a guard — returns an unregister function */
  registerBranchChangeGuard: (guard: BranchChangeGuardFn) => () => void;
  /** Check all guards. Returns true if all allow the change. */
  canChangeBranch: (nextBranchId: string) => boolean;
};

/**
 * Window event fired by `api.service` when the server answers
 * `BRANCH_ACCESS_DENIED`; `detail` is the denied branch id. The branch
 * selector listens so it can drop the branch from its cached reachable
 * list and refetch it from the server.
 */
export const BRANCH_ACCESS_DENIED_EVENT = 'branch:access-denied';

export const DefaultBranchStore = {
  branchOffices: [],
  selectedBranchOfficeId: '',
};

/**
 * Guards live outside the persisted state so they are never serialised.
 * Multiple pages can register guards simultaneously; all must approve.
 */
const branchChangeGuards = new Set<BranchChangeGuardFn>();

const branchStateSlice: StateCreator<
  BranchStoreSlice,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  BranchStoreSlice
> = (set) => ({
  ...DefaultBranchStore,
  setBranchOffices: (branchOffices: BranchOffice[]) =>
    set(
      (state) => {
        // Reconcile the PERSISTED selection against the freshly-loaded
        // (live) branch list. If the selected branch is gone — e.g. it was
        // deleted while active — fall back to '' ("all"/no scope) instead
        // of stranding the operator on a deleted branch (every write would
        // 410 via DeletedBranchWriteInterceptor and reads scope to a dead
        // branch). The branches endpoint returns only non-deleted branches,
        // so an absent id means the selection is no longer valid.
        const selected = state.selectedBranchOfficeId;
        const stillValid =
          !selected ||
          selected === 'all' ||
          branchOffices.some((b) => b.id === selected);
        return {
          branchOffices,
          selectedBranchOfficeId: stillValid ? selected : '',
        };
      },
      undefined,
      'branch/setBranchOffices'
    ),
  setSelectedBranchOfficeId: (id: string) =>
    set(
      () => ({ selectedBranchOfficeId: id }),
      undefined,
      'branch/setSelectedBranchOfficeId'
    ),
  revokeBranch: (branchOfficeId: string) =>
    set(
      (state) => {
        const branchOffices = state.branchOffices.filter(
          (b) => b.id !== branchOfficeId
        );
        const selectedBranchOfficeId =
          state.selectedBranchOfficeId === branchOfficeId
            ? branchOffices[0]?.id ?? ''
            : state.selectedBranchOfficeId;
        return { branchOffices, selectedBranchOfficeId };
      },
      undefined,
      'branch/revokeBranch'
    ),
  registerBranchChangeGuard: (guard: BranchChangeGuardFn) => {
    branchChangeGuards.add(guard);
    return () => {
      branchChangeGuards.delete(guard);
    };
  },
  canChangeBranch: (nextBranchId: string) => {
    for (const guard of branchChangeGuards) {
      if (!guard(nextBranchId)) return false;
    }
    return true;
  },
});

export const useBranchStore = create<BranchStoreSlice>()(
  devtools(persist(branchStateSlice, { name: 'branch-offices' }), {
    name: 'branch-offices',
  })
);

/**
 * Returns true when the currently selected branch is the headquarters (or no
 * specific branch is selected, which means business-wide context).
 * Use to gate create / edit / delete actions — secondary branches are read-only.
 */
export const useIsHQBranch = () => {
  const selectedBranchOfficeId = useBranchStore(
    (state) => state.selectedBranchOfficeId
  );
  const branchOffices = useBranchStore((state) => state.branchOffices);

  if (!selectedBranchOfficeId || selectedBranchOfficeId === 'all') return true;

  const branch = branchOffices.find((b) => b.id === selectedBranchOfficeId);
  return branch?.isHeadquarters ?? true;
};

/**
 * Returns true when the currently selected branch uses shared inventory
 * (managed by HQ). Stock movements and product edits should be disabled.
 */
export const useIsSharedInventory = () => {
  const selectedBranchOfficeId = useBranchStore(
    (state) => state.selectedBranchOfficeId
  );
  const branchOffices = useBranchStore((state) => state.branchOffices);

  if (!selectedBranchOfficeId || selectedBranchOfficeId === 'all') return false;

  const branch = branchOffices.find((b) => b.id === selectedBranchOfficeId);
  if (!branch) return false;

  return !branchOwnsInventory(branch);
};

/**
 * Low-level hook — returns the raw value from the branch store only.
 * Prefer `useBranchOfficeId` from `@app/shared/hooks/use-branch-office-id`
 * which adds an auth-store fallback for users without a branch selector.
 *
 * `branchOfficeId`  — the UUID to pass to API calls (undefined when 'all').
 * `isBranchSelected` — true only when a specific branch is chosen; use as
 *                      `enabled` guard on page-level queries so they don't
 *                      fire before the branch selector has resolved.
 */
export const useBranchOfficeId = () => {
  const selectedBranchOfficeId = useBranchStore(
    (state) => state.selectedBranchOfficeId
  );
  const branchOffices = useBranchStore((state) => state.branchOffices);
  // Current authed user's business — used to detect the case where the
  // persisted branch store was populated for a DIFFERENT business
  // (impersonation swap, sadmin logout→login into another tenant).
  // Both `selectedBranchOfficeId` and `branchOffices` come out of a
  // zustand `persist` middleware, so they survive F5 across identity
  // changes even when handleAuthSuccess never runs for the new tab.
  const currentBusinessId = useAuthStore(
    (state) => state.user?.business?.id ?? state.user?.businessId ?? ''
  );

  // Cross-tenant guard #1 — the persisted `branchOffices` list is
  // stale when its rows point at a different business than the
  // currently authenticated user. STRICT semantics:
  //   • empty list → stale (nothing to verify against)
  //   • empty `currentBusinessId` → auth not resolved yet, refuse to
  //     trust the persisted branch (F5 rehydrates the branch store
  //     before useAuthStore.user finishes loading; injecting the
  //     stale UUID at that moment is what causes the 403).
  //   • any row missing `businessId` → treat as stale (defensive
  //     against legacy persisted rows that predate the field).
  //   • any row's `businessId !== currentBusinessId` → stale.
  const branchesMatchCurrentTenant =
    branchOffices.length > 0 &&
    !!currentBusinessId &&
    branchOffices.every(
      (b) => !!b.businessId && b.businessId === currentBusinessId
    );

  // Cross-tenant guard #2 — even when the list is fresh, verify the
  // selected UUID is one of them. Otherwise the backend
  // `assertBranchOwnership` throws 403 on the very first request
  // after the identity change and the whole page fails to hydrate.
  const isKnownBranch =
    !selectedBranchOfficeId ||
    selectedBranchOfficeId === 'all' ||
    (branchesMatchCurrentTenant &&
      branchOffices.some((b) => b.id === selectedBranchOfficeId));

  return {
    branchOfficeId:
      !selectedBranchOfficeId ||
      selectedBranchOfficeId === 'all' ||
      !isKnownBranch
        ? undefined
        : selectedBranchOfficeId,
    /** True when a specific branch UUID is selected (not empty, not 'all')
     *  AND it belongs to the currently loaded branch list for the
     *  authenticated tenant. Use as `enabled` guard on page-level queries. */
    isBranchSelected:
      !!selectedBranchOfficeId &&
      selectedBranchOfficeId !== 'all' &&
      isKnownBranch,
    /** True when any branch value is set (including 'all') AND, if a
     *  specific UUID is set, it belongs to the current tenant's branch
     *  list. Use as `enabled` guard on layout-level queries. */
    isBranchReady:
      !!selectedBranchOfficeId &&
      (selectedBranchOfficeId === 'all' || isKnownBranch),
  };
};
